import React, {memo, ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'

interface TabPanelProps {
  /** The value of the tab this panel corresponds to */
  value: string
  /** The currently active tab value */
  activeTab: string
  /** Panel content */
  children: ReactNode
  /** Additional CSS class */
  className?: string
  /** Data ID for testing */
  dataId?: string
  /** Whether to keep the panel mounted when inactive to preserve the inner content state */
  keepMounted?: boolean
}

const PanelContainer = styled.div`
  display: none;

  &.--is-active {
    display: block;
  }

  &.--keep-mounted {
    display: block;
    visibility: hidden;
    height: 0;
    overflow: hidden;
    padding: 0;
  }

  &.--keep-mounted.--is-active {
    visibility: visible;
    height: auto;
    overflow: visible;
    padding: ${cssTheme.layout.spacing.s600} 0;
  }
`

const TabPanelComponent = ({
  value,
  activeTab,
  children,
  className,
  dataId,
  keepMounted = false
}: TabPanelProps) => {
  const isActive = activeTab === value

  return (
    <PanelContainer
      className={cx(
        className,
        isActive && '--is-active',
        keepMounted && '--keep-mounted'
      )}
      role="tabpanel"
      aria-labelledby={`tab-${value}`}
      id={`tabpanel-${value}`}
      data-id={dataId}
    >
      {(isActive || keepMounted) && children}
    </PanelContainer>
  )
}

export const TabPanel = memo(TabPanelComponent)

TabPanel.displayName = 'TabPanel'
